import type {
  MandobActivityItem, MandobAddedCustomer, MandobCollectionRow, MandobCurrencyTotal,
  MandobDeliveryRow, MandobFullProfile, MandobOrderRow, MandobPayout,
} from '@erp/shared';
import {
  ArrowLeft, Banknote, BadgePercent, CalendarClock, IdCard, Mail, MapPin, Navigation,
  Receipt, Trash2, Truck, UserPlus, Users, Wallet, type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Badge, Card, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, EmptyState, StatCard } from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { usePermission } from '../../hooks/usePermission.js';
import { formatDate } from '../../lib/intl.js';
import { getMandobFullProfile, removeMandobPayout } from '../../lib/mandob.js';
import { formatMoney, setCurrencyDecimals } from '../../lib/money.js';

import { PayoutDialog } from './PayoutDialog.js';

const ACTIVITY_ICON: Record<MandobActivityItem['type'], LucideIcon> = {
  sale: Receipt,
  collection: Banknote,
  delivery: Truck,
  customer: UserPlus,
  payout: Wallet,
};
const ACTIVITY_TONE: Record<MandobActivityItem['type'], string> = {
  sale: 'var(--color-info)',
  collection: 'var(--color-success)',
  delivery: 'var(--color-fg-muted)',
  customer: 'var(--color-warning)',
  payout: 'var(--color-brand)',
};

/**
 * Office profile of ONE field rep (مندوب) — the detail view behind each row of
 * /mandob. A read-only aggregation of everything tied to the rep: identity + pay,
 * their field sales and collections (per-currency), deliveries, the customers
 * they registered, payout history, and a merged recent-activity feed.
 */
export function MandobProfilePage(): JSX.Element {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const dir = useLayoutDirection();

  const [data, setData] = useState<MandobFullProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const canManage = usePermission('mandob.manage');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    void getMandobFullProfile(id)
      .then((d) => {
        if (!active) return;
        // Merge any currency decimals the payload carries (keeps IQD → 0 etc. even
        // for a currency the global /currencies load hasn't registered).
        setCurrencyDecimals(d.currencyDecimals);
        setData(d);
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  /**
   * Refetch WITHOUT the loading skeleton.
   *
   * Paying or removing changes the tiles, the payout list and the activity feed
   * at once, so the whole payload has to come back — but flipping to a skeleton
   * for it would throw the reader out of whatever tab they were on.
   */
  const refresh = useCallback(async (): Promise<void> => {
    try {
      const d = await getMandobFullProfile(id);
      setCurrencyDecimals(d.currencyDecimals);
      setData(d);
    } catch { /* the page keeps showing what it had */ }
  }, [id]);

  const money = (a: string, c: string | null): string =>
    c ? formatMoney(a, { locale: i18n.language, currency: c }) : a;
  const date = (d: string): string => formatDate(d, { locale: i18n.language });

  const backLink = (
    <Link
      to="/mandob"
      className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-muted transition hover:text-fg"
    >
      <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} aria-hidden />
      {t('title', { defaultValue: 'Mandobs' })}
    </Link>
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-5">
        {backLink}
        <Card className="p-6"><Skeleton className="h-8 w-56" /><Skeleton className="mt-4 h-40 w-full" /></Card>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="flex flex-col gap-5">
        {backLink}
        <EmptyState
          title={t('profile.notFound', { defaultValue: 'Rep not found' })}
          action={<Link to="/mandob"><Button>{t('profile.backToList', { defaultValue: 'Back to list' })}</Button></Link>}
        />
      </div>
    );
  }

  const { profile, earnings, baseCurrencyCode, sales, collections, deliveries, customers, payouts, activity } = data;
  const base = baseCurrencyCode;
  const initials =
    profile.name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('') || '?';

  // Per-currency money summary rendered as a compact "8,000 IQD · $12" string.
  const totalsLabel = (list: MandobCurrencyTotal[]): string =>
    list.length === 0 ? '—' : list.map((c) => money(c.total, c.currencyCode)).join(' · ');

  /**
   * Undo a payout.
   *
   * Confirmed first and spelled out in the message, because this is not a
   * tidy-up: it reverses a journal entry and hands the commission back, so the
   * rep is owed that money again.
   */
  const onRemovePayout = async (p: MandobPayout): Promise<void> => {
    const ok = await confirmDialog({
      title: t('payout.removeTitle', { defaultValue: 'Remove this payout?' }),
      message: t('payout.removeConfirm', {
        amount: money(p.total, base),
        defaultValue: 'The {{amount}} payout is reversed in the accounts and the commission goes back to being unpaid.',
      }),
      confirmLabel: t('payout.remove', { defaultValue: 'Remove payout' }),
      tone: 'danger',
    });
    if (!ok) return;
    setRemoving(p.id);
    try {
      await removeMandobPayout(id, p.id);
      toast.success(t('payout.removed', { defaultValue: 'Payout removed' }));
      await refresh();
    } catch (e) {
      // Both codes mean the same thing to the reader: somebody got there first.
      // 409 is the concurrent case (two clicks racing past the same check); 404
      // is the settled one, because a removed payout is no longer in the list
      // the controller looks it up in.
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'conflict' || code === 'not_found'
        ? t('payout.alreadyRemoved', { defaultValue: 'That payout was already removed.' })
        : tc('errors:server'));
    } finally { setRemoving(null); }
  };

  return (
    <div className="flex flex-col gap-5">
      {backLink}

      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-border bg-gradient-to-br from-brand/5 via-bg to-bg p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand to-accent text-xl font-bold text-brand-contrast shadow-md ring-1 ring-brand/20">
            {initials}
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{profile.name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <Badge variant="info">{t('profile.role', { defaultValue: 'Field sales rep' })}</Badge>
              <Badge variant={profile.payScheme === 'salary_commission' ? 'info' : 'secondary'}>
                {t(`scheme.${profile.payScheme}`, { defaultValue: profile.payScheme })}
              </Badge>
              <Badge variant={profile.isActive ? 'success' : 'secondary'}>
                {tc(`status.${profile.isActive ? 'active' : 'inactive'}`, { defaultValue: profile.isActive ? 'Active' : 'Inactive' })}
              </Badge>
              {profile.email && (
                <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-1 text-xs transition hover:text-brand" dir="ltr">
                  <Mail className="h-3 w-3" aria-hidden />{profile.email}
                </a>
              )}
              {profile.territory && (
                <span className="inline-flex items-center gap-1 text-xs">
                  <Navigation className="h-3 w-3" aria-hidden />{profile.territory}
                </span>
              )}
            </div>
          </div>
        </div>
        {/* Paying used to live only on the roster, so settling up with somebody
            meant leaving the page that tells you what they are owed. */}
        {canManage && (
          <Button onClick={() => setPaying(true)}>
            {t('payout.pay', { defaultValue: 'Pay' })}
          </Button>
        )}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label={t('profile.tiles.sales', { defaultValue: 'Sales' })}
          value={sales.count}
          hint={totalsLabel(sales.byCurrency)}
          icon={Receipt}
          tone="info"
        />
        <StatCard
          label={t('profile.tiles.collections', { defaultValue: 'Collected' })}
          value={collections.count}
          hint={totalsLabel(collections.byCurrency)}
          icon={Banknote}
          tone="success"
        />
        <StatCard
          label={t('profile.tiles.commission', { defaultValue: 'Unpaid commission' })}
          value={money(earnings.commissionAccrued, base)}
          hint={profile.payScheme === 'salary_commission'
            ? t('field.rateHint', { rate: profile.commissionRate, defaultValue: `${profile.commissionRate}% of collections` })
            : t('scheme.salary', { defaultValue: 'Salary only' })}
          icon={BadgePercent}
          tone="brand"
        />
        <StatCard
          label={t('profile.tiles.customers', { defaultValue: 'Customers added' })}
          value={customers.count}
          icon={Users}
          tone="warning"
        />
        <StatCard
          label={t('profile.tiles.deliveries', { defaultValue: 'Deliveries' })}
          value={deliveries.count}
          hint={deliveries.count > 0
            ? `${t('profile.delivered', { defaultValue: 'Delivered' })} ${deliveries.delivered} · ${t('profile.failed', { defaultValue: 'Failed' })} ${deliveries.failed}`
            : undefined}
          icon={Truck}
          tone="neutral"
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="h-10 flex-wrap">
          <TabsTrigger value="overview" className="gap-1.5"><IdCard className="h-4 w-4" aria-hidden />{t('profile.tabs.overview', { defaultValue: 'Overview' })}</TabsTrigger>
          <TabsTrigger value="sales" className="gap-1.5"><Receipt className="h-4 w-4" aria-hidden />{t('profile.tabs.sales', { defaultValue: 'Sales' })}</TabsTrigger>
          <TabsTrigger value="collections" className="gap-1.5"><Banknote className="h-4 w-4" aria-hidden />{t('profile.tabs.collections', { defaultValue: 'Collections' })}</TabsTrigger>
          <TabsTrigger value="deliveries" className="gap-1.5"><Truck className="h-4 w-4" aria-hidden />{t('profile.tabs.deliveries', { defaultValue: 'Deliveries' })}</TabsTrigger>
          <TabsTrigger value="customers" className="gap-1.5"><Users className="h-4 w-4" aria-hidden />{t('profile.tabs.customers', { defaultValue: 'Customers' })}</TabsTrigger>
          <TabsTrigger value="payouts" className="gap-1.5"><Wallet className="h-4 w-4" aria-hidden />{t('profile.tabs.payouts', { defaultValue: 'Payouts' })}</TabsTrigger>
        </TabsList>

        {/* ── Overview: identity/pay + per-currency breakdowns + activity feed ── */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card>
              <SectionTitle icon={IdCard}>{t('profile.identity', { defaultValue: 'Identity & pay' })}</SectionTitle>
              <dl className="flex flex-col gap-0.5 text-sm">
                <InfoRow icon={Mail} label={t('create.email', { defaultValue: 'Email' })} value={profile.email} href={profile.email ? `mailto:${profile.email}` : undefined} />
                <InfoRow icon={Navigation} label={t('columns.territory', { defaultValue: 'Territory' })} value={profile.territory} />
                <InfoRow icon={Wallet} label={t('columns.salary', { defaultValue: 'Salary' })} value={money(profile.salary, base)} />
                <InfoRow
                  icon={BadgePercent}
                  label={t('create.commissionRate', { defaultValue: 'Commission %' })}
                  value={profile.payScheme === 'salary_commission' ? `${profile.commissionRate}%` : '—'}
                />
                <InfoRow icon={Banknote} label={t('field.lifetimeCommission', { defaultValue: 'Lifetime commission' })} value={money(earnings.commissionLifetime, base)} />
                <InfoRow icon={CalendarClock} label={t('profile.since', { defaultValue: 'On file since' })} value={date(profile.createdAt)} />
              </dl>
            </Card>

            <Card>
              <SectionTitle icon={Receipt}>{t('profile.breakdown', { defaultValue: 'By currency' })}</SectionTitle>
              <div className="flex flex-col gap-4 text-sm">
                <CurrencyBreakdown
                  title={t('profile.tabs.sales', { defaultValue: 'Sales' })}
                  rows={sales.byCurrency}
                  empty={t('profile.empty.sales', { defaultValue: 'No sales yet' })}
                  money={money}
                  countLabel={t('profile.cols.docs', { defaultValue: 'docs' })}
                />
                <CurrencyBreakdown
                  title={t('profile.tabs.collections', { defaultValue: 'Collections' })}
                  rows={collections.byCurrency}
                  empty={t('profile.empty.collections', { defaultValue: 'No collections yet' })}
                  money={money}
                  countLabel={t('profile.cols.docs', { defaultValue: 'docs' })}
                />
              </div>
            </Card>

            <Card>
              <SectionTitle icon={CalendarClock}>{t('profile.activity', { defaultValue: 'Recent activity' })}</SectionTitle>
              {activity.length === 0 ? (
                <p className="py-4 text-sm text-fg-muted">{t('profile.noActivity', { defaultValue: 'No activity yet.' })}</p>
              ) : (
                <ol className="flex flex-col gap-3">
                  {activity.map((a) => {
                    const Icon = ACTIVITY_ICON[a.type];
                    return (
                      <li key={`${a.type}-${a.id}`} className="flex items-start gap-3">
                        <span
                          className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg"
                          style={{ background: `color-mix(in oklab, ${ACTIVITY_TONE[a.type]} 16%, transparent)`, color: ACTIVITY_TONE[a.type] }}
                        >
                          <Icon className="h-4 w-4" aria-hidden />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-sm font-medium text-fg">{a.title}</span>
                            {a.amount != null && (
                              <span className="shrink-0 font-mono text-sm tabular-nums text-fg">{money(a.amount, a.currencyCode ?? base)}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-fg-muted">
                            <span>{t(`profile.activityType.${a.type}`, { defaultValue: a.type })}</span>
                            <span aria-hidden>·</span>
                            <span>{date(a.date)}</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* ── Sales ── */}
        <TabsContent value="sales">
          {sales.recent.length === 0 ? (
            <Card className="p-6"><EmptyState title={t('profile.empty.sales', { defaultValue: 'No sales yet' })} /></Card>
          ) : (
            <Card className="p-0">
              <DataTable
                columns={salesColumns(t, money, date)}
                data={sales.recent}
                rowKey={(r: MandobOrderRow) => r.id}
                initialSort={{ id: 'date', dir: 'desc' }}
              />
            </Card>
          )}
        </TabsContent>

        {/* ── Collections ── */}
        <TabsContent value="collections">
          {collections.recent.length === 0 ? (
            <Card className="p-6"><EmptyState title={t('profile.empty.collections', { defaultValue: 'No collections yet' })} /></Card>
          ) : (
            <Card className="p-0">
              <DataTable
                columns={collectionsColumns(t, money, date)}
                data={collections.recent}
                rowKey={(r: MandobCollectionRow) => r.id}
                initialSort={{ id: 'date', dir: 'desc' }}
              />
            </Card>
          )}
        </TabsContent>

        {/* ── Deliveries ── */}
        <TabsContent value="deliveries">
          {deliveries.recent.length === 0 ? (
            <Card className="p-6"><EmptyState title={t('profile.empty.deliveries', { defaultValue: 'No deliveries yet' })} /></Card>
          ) : (
            <Card className="p-0">
              <DataTable
                columns={deliveriesColumns(t, money, date)}
                data={deliveries.recent}
                rowKey={(r: MandobDeliveryRow) => r.id}
                initialSort={{ id: 'date', dir: 'desc' }}
              />
            </Card>
          )}
        </TabsContent>

        {/* ── Customers added ── */}
        <TabsContent value="customers">
          {customers.recent.length === 0 ? (
            <Card className="p-6"><EmptyState title={t('profile.empty.customers', { defaultValue: 'No customers added yet' })} /></Card>
          ) : (
            <Card className="p-0">
              <DataTable
                columns={customersColumns(t, date)}
                data={customers.recent}
                rowKey={(r: MandobAddedCustomer) => r.id}
                initialSort={{ id: 'added', dir: 'desc' }}
              />
            </Card>
          )}
        </TabsContent>

        {/* ── Payouts ── */}
        <TabsContent value="payouts">
          {payouts.length === 0 ? (
            <Card className="p-6"><EmptyState title={t('profile.empty.payouts', { defaultValue: 'No payouts yet' })} /></Card>
          ) : (
            <Card className="p-0">
              <DataTable
                columns={payoutsColumns(t, (a) => money(a, base), date, canManage ? onRemovePayout : undefined, removing)}
                data={payouts}
                rowKey={(r: MandobPayout) => r.id}
                initialSort={{ id: 'date', dir: 'desc' }}
              />
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {paying && (
        <PayoutDialog
          mandob={profile}
          onClose={() => setPaying(false)}
          onDone={refresh}
        />
      )}
    </div>
  );
}

type TFn = ReturnType<typeof useTranslation>['t'];
type MoneyFn = (a: string, c: string | null) => string;
type DateFn = (d: string) => string;

function salesColumns(t: TFn, money: MoneyFn, date: DateFn) {
  return [
    {
      id: 'number', header: t('profile.cols.number', { defaultValue: 'Number' }),
      cell: (r: MandobOrderRow) => (
        <Link to={`/sales/orders/${r.id}`} className="font-mono font-medium text-brand hover:underline">{r.docNumber}</Link>
      ),
      searchValue: (r: MandobOrderRow) => `${r.docNumber} ${r.partyName}`,
    },
    {
      id: 'customer', header: t('profile.cols.customer', { defaultValue: 'Customer' }),
      cell: (r: MandobOrderRow) => (
        <Link to={`/sales/parties/${r.partyId}`} className="hover:text-brand hover:underline">{r.partyName || '—'}</Link>
      ),
      sortValue: (r: MandobOrderRow) => r.partyName,
    },
    { id: 'date', header: t('profile.cols.date', { defaultValue: 'Date' }), cell: (r: MandobOrderRow) => <span className="whitespace-nowrap text-fg-muted">{date(r.docDate)}</span>, sortValue: (r: MandobOrderRow) => r.docDate },
    {
      id: 'status', header: t('profile.cols.status', { defaultValue: 'Status' }),
      cell: (r: MandobOrderRow) => <Badge variant="secondary">{r.status}</Badge>,
    },
    { id: 'total', header: t('profile.cols.total', { defaultValue: 'Total' }), align: 'end' as const, cell: (r: MandobOrderRow) => <span className="font-mono tabular-nums">{money(r.total, r.currencyCode)}</span>, sortValue: (r: MandobOrderRow) => Number(r.total) },
  ];
}

function collectionsColumns(t: TFn, money: MoneyFn, date: DateFn) {
  return [
    { id: 'number', header: t('profile.cols.number', { defaultValue: 'Number' }), cell: (r: MandobCollectionRow) => <span className="font-mono font-medium">{r.docNumber}</span>, searchValue: (r: MandobCollectionRow) => `${r.docNumber} ${r.partyName}` },
    {
      id: 'customer', header: t('profile.cols.customer', { defaultValue: 'Customer' }),
      cell: (r: MandobCollectionRow) => (
        <Link to={`/sales/parties/${r.partyId}`} className="hover:text-brand hover:underline">{r.partyName || '—'}</Link>
      ),
      sortValue: (r: MandobCollectionRow) => r.partyName,
    },
    { id: 'date', header: t('profile.cols.date', { defaultValue: 'Date' }), cell: (r: MandobCollectionRow) => <span className="whitespace-nowrap text-fg-muted">{date(r.paymentDate)}</span>, sortValue: (r: MandobCollectionRow) => r.paymentDate },
    { id: 'amount', header: t('profile.cols.amount', { defaultValue: 'Amount' }), align: 'end' as const, cell: (r: MandobCollectionRow) => <span className="font-mono font-semibold tabular-nums text-success">{money(r.amount, r.currencyCode)}</span>, sortValue: (r: MandobCollectionRow) => Number(r.amount) },
  ];
}

function deliveriesColumns(t: TFn, money: MoneyFn, date: DateFn) {
  return [
    { id: 'number', header: t('profile.cols.number', { defaultValue: 'Number' }), cell: (r: MandobDeliveryRow) => <span className="font-mono font-medium">{r.docNumber}</span>, searchValue: (r: MandobDeliveryRow) => r.docNumber },
    {
      id: 'status', header: t('profile.cols.status', { defaultValue: 'Status' }),
      cell: (r: MandobDeliveryRow) => (
        <Badge variant={r.status === 'delivered' ? 'success' : r.status === 'failed' ? 'danger' : 'secondary'}>
          {t(`profile.deliveryStatus.${r.status}`, { defaultValue: r.status })}
        </Badge>
      ),
      sortValue: (r: MandobDeliveryRow) => r.status,
    },
    { id: 'date', header: t('profile.cols.date', { defaultValue: 'Date' }), cell: (r: MandobDeliveryRow) => <span className="whitespace-nowrap text-fg-muted">{date(r.deliveredAt ?? r.createdAt)}</span>, sortValue: (r: MandobDeliveryRow) => r.deliveredAt ?? r.createdAt },
    { id: 'total', header: t('profile.cols.total', { defaultValue: 'Total' }), align: 'end' as const, cell: (r: MandobDeliveryRow) => <span className="font-mono tabular-nums">{r.orderTotal != null ? money(r.orderTotal, r.currencyCode) : '—'}</span>, sortValue: (r: MandobDeliveryRow) => (r.orderTotal != null ? Number(r.orderTotal) : null) },
  ];
}

function customersColumns(t: TFn, date: DateFn) {
  return [
    {
      id: 'name', header: t('profile.cols.customer', { defaultValue: 'Customer' }),
      cell: (r: MandobAddedCustomer) => (
        <Link to={`/sales/parties/${r.id}`} className="min-w-0">
          <div className="truncate font-medium text-fg hover:text-brand hover:underline">{r.displayName}</div>
          <div className="truncate font-mono text-xs text-fg-muted">{r.code}</div>
        </Link>
      ),
      searchValue: (r: MandobAddedCustomer) => `${r.displayName} ${r.code} ${r.phone ?? ''}`,
      sortValue: (r: MandobAddedCustomer) => r.displayName,
    },
    { id: 'phone', header: t('profile.cols.phone', { defaultValue: 'Phone' }), cell: (r: MandobAddedCustomer) => (r.phone ? <span dir="ltr" className="text-fg-muted">{r.phone}</span> : '—') },
    {
      id: 'location', header: t('profile.cols.location', { defaultValue: 'Location' }),
      cell: (r: MandobAddedCustomer) => (r.latitude != null && r.longitude != null ? (
        <a
          href={`https://www.openstreetmap.org/?mlat=${r.latitude}&mlon=${r.longitude}#map=15/${r.latitude}/${r.longitude}`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <MapPin className="h-3.5 w-3.5" aria-hidden />{t('profile.viewOnMap', { defaultValue: 'Map' })}
        </a>
      ) : '—'),
    },
    { id: 'added', header: t('profile.cols.added', { defaultValue: 'Added' }), align: 'end' as const, cell: (r: MandobAddedCustomer) => <span className="whitespace-nowrap text-fg-muted">{date(r.createdAt)}</span>, sortValue: (r: MandobAddedCustomer) => r.createdAt },
  ];
}

function payoutsColumns(
  t: TFn,
  moneyBase: (a: string) => string,
  date: DateFn,
  onRemove?: (p: MandobPayout) => void,
  removingId?: string | null,
) {
  return [
    { id: 'date', header: t('profile.cols.date', { defaultValue: 'Date' }), cell: (r: MandobPayout) => <span className="whitespace-nowrap text-fg-muted">{date(r.createdAt)}</span>, sortValue: (r: MandobPayout) => r.createdAt },
    {
      id: 'period', header: t('profile.cols.period', { defaultValue: 'Period' }),
      cell: (r: MandobPayout) => (r.periodStart || r.periodEnd ? <span className="text-fg-muted">{[r.periodStart, r.periodEnd].filter((v): v is string => Boolean(v)).map(date).join(' – ')}</span> : '—'),
    },
    { id: 'salary', header: t('profile.cols.salary', { defaultValue: 'Salary' }), align: 'end' as const, cell: (r: MandobPayout) => <span className="font-mono tabular-nums">{moneyBase(r.salaryAmount)}</span>, sortValue: (r: MandobPayout) => Number(r.salaryAmount) },
    { id: 'commission', header: t('profile.cols.commission', { defaultValue: 'Commission' }), align: 'end' as const, cell: (r: MandobPayout) => <span className="font-mono tabular-nums text-fg-muted">{moneyBase(r.commissionAmount)}</span>, sortValue: (r: MandobPayout) => Number(r.commissionAmount) },
    { id: 'total', header: t('profile.cols.total', { defaultValue: 'Total' }), align: 'end' as const, cell: (r: MandobPayout) => <span className="font-mono font-semibold tabular-nums">{moneyBase(r.total)}</span>, sortValue: (r: MandobPayout) => Number(r.total) },
    ...(onRemove ? [{
      id: 'actions', header: '', align: 'end' as const,
      cell: (r: MandobPayout) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-danger hover:text-danger"
          loading={removingId === r.id}
          disabled={removingId !== null}
          onClick={() => onRemove(r)}
          aria-label={t('payout.remove', { defaultValue: 'Remove payout' })}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      ),
    }] : []),
  ];
}

function CurrencyBreakdown({ title, rows, empty, money, countLabel }: {
  title: string;
  rows: MandobCurrencyTotal[];
  empty: string;
  money: MoneyFn;
  countLabel: string;
}): JSX.Element {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold text-fg-muted">{title}</div>
      {rows.length === 0 ? (
        <span className="text-fg-muted">{empty}</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((r) => (
            <li key={r.currencyCode} className="flex items-center justify-between gap-2">
              <span className="text-fg-muted">{r.count} {countLabel}</span>
              <span className="font-mono tabular-nums text-fg">{money(r.total, r.currencyCode)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, children }: { icon?: LucideIcon; children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="mb-4 flex items-center gap-2 border-b border-border/60 pb-2.5 text-[0.7rem] font-bold uppercase tracking-wider text-fg-subtle">
      {Icon && <Icon className="h-3.5 w-3.5 text-brand" aria-hidden />}
      {children}
    </h2>
  );
}

function InfoRow({ icon: Icon, label, value, href }: {
  icon: LucideIcon;
  label: string;
  value?: string | null;
  href?: string;
}): JSX.Element {
  const isLink = Boolean(value && href);
  return (
    <div className="group flex items-center gap-3 rounded-lg px-1 py-1.5 transition-colors hover:bg-bg-subtle/60">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-bg-subtle text-fg-subtle transition-colors group-hover:bg-brand/10 group-hover:text-brand">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-[0.68rem] font-medium uppercase tracking-wide text-fg-subtle">{label}</dt>
        <dd className="truncate text-sm font-semibold text-fg">
          {value ? (
            isLink ? (
              <a href={href} className="underline-offset-4 transition-colors hover:text-brand hover:underline" dir="ltr">{value}</a>
            ) : value
          ) : (
            <span className="font-normal text-fg-muted">—</span>
          )}
        </dd>
      </div>
    </div>
  );
}
